import {Component, OnInit} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {of} from 'rxjs';
import {filter, switchMap, takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {PopulationFilterSelector, PopulationFilterTransId, PopulationFilterValue} from './context-bar.interfaces';

import {PayTypeSelector, PayTypeTranslation, PayTypeWithCliRegIds} from '../../../+payrolls/payrolls.model';
import {ALL_OPERATORS} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {RolesService} from '../../../shared/authentication/roles/roles.service';
import {CookieService} from '../../../shared/cookie-service/cookie.service';
import {EmployeesContextUtilsService} from '../../../shared/employees/utils/employee-context-utils.service';
import {LocalizationService} from '../../../shared/localization/localization.service';
import {
  PopulationCriteria,
  PopulationCriterion,
  POPULATION_CRITERIA_COOKIE_NAME
} from '../../../shared/population/create-population/create-population.interfaces';

@Component({
  selector: 'gp-context-bar',
  templateUrl: './context-bar.component.html',
  styleUrls: ['./context-bar.component.scss'],
})
export class ContextBarComponent extends Destroyable implements OnInit {

  public hasPayrollRole: boolean = false;
  public hasPopulationFilterRoles: boolean = false;
  public payTypeTranslation: PayTypeTranslation;
  public currentPayType: string = 'ALL';
  public currentPopulation: PopulationFilterSelector;
  public populationCriteriaCookie: PopulationCriteria;
  public selectedFilterCount = 0;
  public selectedFilters: PopulationCriterion[] = [];

  constructor(private rolesService: RolesService,
              private translate: TranslateService,
              private cookieService: CookieService,
              private employeesContextUtilsService: EmployeesContextUtilsService,
              private localization: LocalizationService) {
    super();
  }

  public ngOnInit(): void {
    this.employeesContextUtilsService.resetSelectedFilters$.pipe(
      tap(() => {
        this.selectedFilterCount = 0;
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    if (this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME)) {
      this.populationCriteriaCookie = JSON.parse(this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME));
      this.selectedFilters = this.populationCriteriaCookie?.criteria;
      this.selectedFilterCount = this.populationCriteriaCookie?.criteria.length;
    }

    if (this.rolesService.hasRole('PEOPLE_READ', 'PEOPLE_WRITE', 'PEOPLE_PARTIAL_UPDATE')) {
      this.hasPopulationFilterRoles = true;
    }

    // To be deleted after all users have been deleted the old cookie with path /ui
    if (this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME)) {
      this.cookieService.deleteCookie(POPULATION_CRITERIA_COOKIE_NAME, '/ui');
    }
    //

    if (this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME)) {
      this.employeesContextUtilsService.updateContextPopulation({
        value: PopulationFilterValue.CUSTOM,
        transId: PopulationFilterTransId.CUSTOM
      });
    } else {
      this.employeesContextUtilsService.updateContextPopulation({
        value: PopulationFilterValue.ALL,
        transId: PopulationFilterTransId.ALL
      });
    }


    if (this.rolesService.hasRole('PAYROLL_READ', 'PAYROLL_WRITE', 'POSTPAY_PAYMENTS')) {
      this.hasPayrollRole = true;

      const initialContextPayType = this.employeesContextUtilsService.getContextPayType();

      if (initialContextPayType.transId) {
        this.currentPayType = initialContextPayType.transId;

        this.getInitialContextPayTypeTranslation(initialContextPayType.transId);

        // Translation got so far ain't guaranteed to be the good one,
        // as it seems we use local translation with default language until api translation is loaded for the first time
        // so we check langChange
        this.translate.onLangChange
          .asObservable()
          .pipe(
            switchMap(() => {
                if (this.currentPayType !== 'ALL') {
                  return this.translate.get(this.currentPayType);
                }
                return of('');
              }
            ),
            filter(translation => !!translation),
            takeUntil(this.componentDestroyed$),
          ).subscribe(translation => {
            this.payTypeTranslation = this.setPayTypeTranslation(false, translation);
          }
        );
      } else {
        this.currentPayType = 'ALL';
        this.payTypeTranslation = this.setPayTypeTranslation(true);
      }

      this.listenContextPayType();
    }

    this.employeesContextUtilsService.getContextPopulation().pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe((value: PopulationFilterSelector) => this.currentPopulation = value);
  }

  public clearAllFilters(): void {
    this.employeesContextUtilsService.updateContextPopulation(
      {value: PopulationFilterValue.ALL, transId: PopulationFilterTransId.ALL}
    );
    this.cookieService.deleteCookie(POPULATION_CRITERIA_COOKIE_NAME, '/');
    this.selectedFilterCount = 0;
    this.employeesContextUtilsService.refreshPopulation('/');
  }

  private getInitialContextPayTypeTranslation(transId: string) {
    this.translate.get(transId)
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(contextPayTypeTranslation => {
        this.setInitialContextPayType(contextPayTypeTranslation);
      });
  }

  private setInitialContextPayType(translation: string) {
    this.payTypeTranslation = this.setPayTypeTranslation(false, translation);
  }

  private setPayTypeTranslation(isAll: boolean, payType: string = ''): PayTypeTranslation {
    const key = isAll ? 'CONTEXT_BAR.working_on_pay_type_all' : 'CONTEXT_BAR.working_on_pay_type';
    const args = {
      payType: payType
    };
    return {key, args};
  }

  private listenContextPayType() {
    this.employeesContextUtilsService.contextPayType$
      .pipe(
        filter(payType => payType != null)
      )
      .subscribe((payType: PayTypeSelector) => {
        if (payType === 'ALL') {
          this.currentPayType = 'ALL';
          this.payTypeTranslation = this.setPayTypeTranslation(true);
        } else {
          this.currentPayType = (payType as PayTypeWithCliRegIds).transId;
          this.payTypeTranslation = this.setPayTypeTranslation(
            false,
            this.translate.instant((payType as PayTypeWithCliRegIds).transId));
        }
      });
  }

  public isCustomPopulationSelected(): boolean {
    return this.currentPopulation.value === PopulationFilterValue.CUSTOM;
  }

  public getOperatorSymbol(operatorText): string {
    return ALL_OPERATORS.filter(op => op.value === operatorText)[0]?.display;
  }

  public getLocalizedDate(date: string): string {
    return moment(date).format(this.localization.dateFormat);
  }

  public getTranslatedZoneName({translatedZoneName = null, zoneName = null}: PopulationCriterion): string {
    if (translatedZoneName) {
      const parts = translatedZoneName.split('/');
      return parts.map(part => this.translate.instant(part.trim())).join(' / ');
    }
    if (zoneName) {
      return this.translate.instant(zoneName.replace(/_/g, ''));
    }
    return '';
  }
}
